import { z } from 'zod';
import { InvoiceDocumentTypeEnum } from './invoice-blob.js';

/**
 * Wire-format of a single pipeline-queue job.
 *
 * The producer (`apps/api/src/lib/queue-producer.ts`) emits this object on
 * the `PIPELINE` queue after a successful upload (#29); the consumer
 * (`apps/api/src/pipeline/consumer.ts`) parses incoming messages against
 * this schema before dispatching to the format-detection / extraction
 * stages (#30+).
 *
 * Keeping `reprocess` in the schema today (even though no producer sets it
 * to `true` in v1) lets the consumer treat the flag as authoritative once
 * the explicit reprocess endpoint (#48) lands - the wire form stays stable.
 */
export const PipelineJobSchema = z.object({
  /** ULID of the `invoices` row to process. */
  invoiceId: z.string().min(1),
  /**
   * Reprocess flag (#48): when true the consumer bypasses the
   * idempotency / status guards and re-runs extraction on the same row.
   * Defaults to false; set by the operator-driven reprocess endpoint.
   */
  reprocess: z.boolean().default(false),
  /** Optional supplier-name hint preserved across reprocess of known rows. */
  supplierNameHint: z.string().min(1).optional(),
  /** Optional SAP supplier id carried across reprocess for the same invoice. */
  sapSupplierIdHint: z.string().min(1).optional(),
  /** Optional module context from upload/email routing for LLM document-type fallback. */
  documentTypeHint: InvoiceDocumentTypeEnum.optional(),
});

export type PipelineJob = z.infer<typeof PipelineJobSchema>;
